import EmailComposerTemplateControls from '@/components/affiliate/bulk-email/EmailComposerTemplateControls';
import EmailEditorComposer from '@/components/affiliate/bulk-email/EmailEditorComposer';
import { MAX_BODY_LENGTH } from '@/components/affiliate/bulk-email/editor-config';
import { EmailConnectPrompt } from '@/components/integrations/EmailConnectPrompt';
import SaveAsTemplatePanel from '@/components/templates/SaveAsTemplatePanel';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import type { useEmailComposer } from '@/hooks/use-email-composer';
import { Box, HStack, Text, VisuallyHidden } from '@chakra-ui/react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import EmailComposerCanvas from './EmailComposerCanvas';
import EmailComposerSubjectRow from './EmailComposerSubjectRow';
import { EMAIL_COMPOSER_ROW_LABEL_WIDTH } from './email-composer-layout';

/** The copy that differs between bulk composers: who the email goes to, and how many. */
export interface EmailComposerDialogShellLabels {
  backToEdit: string;
  send: string;
  confirmTitle: string;
  confirmDescription: string;
  confirmSend: string;
}

interface EmailComposerDialogShellProps {
  composer: ReturnType<typeof useEmailComposer>;
  isOpen: boolean;
  /** Visually hidden dialog title. */
  title: string;
  /** False renders `recapSlot`, or the connect prompt when there is none. */
  showComposer: boolean;
  labels: EmailComposerDialogShellLabels;
  recipientSlot: ReactNode;
  programSlot?: ReactNode;
  recapSlot?: ReactNode;
}

/**
 * Dialog chrome shared by the bulk email composers (affiliates, favorites,
 * invitations): the sized dialog, the sender row, subject and body editors, the
 * save-as-template overlay and the swap to a recap or a connect prompt. Each
 * surface keeps its own recipients, send mode and recap.
 */
const EmailComposerDialogShell: FC<EmailComposerDialogShellProps> = ({
  composer,
  isOpen,
  title,
  showComposer,
  labels,
  recipientSlot,
  programSlot,
  recapSlot,
}) => {
  const { t } = useTranslation();

  const counterSlot =
    composer.bodyLength > 0 ? (
      <Text fontSize="xs" color={composer.counterColor} textAlign="right">
        {composer.bodyLength.toLocaleString()} / {MAX_BODY_LENGTH.toLocaleString()}
      </Text>
    ) : undefined;

  const bodySlot = (
    <EmailEditorComposer
      resetKey={composer.bodySeedKey}
      availableVariables={composer.availableVariables}
      attachments={composer.attachments}
      isPreviewing={composer.isPreviewing}
      previewHtml={composer.previewCompiled.body}
      onBodyChange={composer.setBody}
      onTogglePreview={composer.handleTogglePreview}
      onFileSelect={composer.handleFileSelect}
      onRemoveAttachment={composer.handleRemoveAttachment}
      deletingId={composer.deletingId}
      isUploading={composer.isUploading}
      attachmentStatus={
        composer.attachmentControls
          ? {
              issues: composer.attachmentControls.issues,
              onDismissIssue: composer.attachmentControls.dismissIssue,
              validationError: composer.attachmentControls.validationError,
              maxTotalBytes: composer.attachmentControls.maxTotalBytes,
            }
          : undefined
      }
      initialContent={composer.initialContent}
      onSnapshotChange={composer.onSnapshotChange}
      variant="document"
      footerCounterSlot={counterSlot}
      footerTemplateSlot={
        <EmailComposerTemplateControls
          templateFlow={composer.templateFlow}
          disabled={composer.isPreviewing}
        />
      }
    />
  );

  const senderSlot = composer.senderEmail ? (
    <HStack minW={0} gap={3} minH="28px">
      <Text fontSize="sm" color="fg.muted" w={EMAIL_COMPOSER_ROW_LABEL_WIDTH} flexShrink={0}>
        {t('bulkEmail.compose.from')}:
      </Text>
      <Text fontSize="sm" fontWeight="medium" truncate>
        {composer.senderEmail}
      </Text>
    </HStack>
  ) : undefined;

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && composer.handleClose()}
      closeOnInteractOutside={false}
      size={showComposer ? 'xl' : 'md'}
      placement="center"
    >
      <DialogContent
        overflow="hidden"
        maxW={showComposer ? { base: 'calc(100vw - 24px)', md: '900px', xl: '980px' } : 'md'}
        h={showComposer ? { base: 'calc(100dvh - 24px)', md: '82vh' } : undefined}
        maxH={showComposer ? '860px' : undefined}
      >
        <DialogHeader p={0}>
          <VisuallyHidden>
            <DialogTitle>{title}</DialogTitle>
          </VisuallyHidden>
          {!showComposer ? <DialogCloseTrigger /> : null}
        </DialogHeader>

        {showComposer ? (
          <DialogBody p={{ base: 3, md: 4 }} overflow="hidden" h="full">
            <EmailComposerCanvas
              step={composer.step}
              editLabel={
                composer.step === 'confirm' ? labels.backToEdit : t('bulkEmail.compose.edit')
              }
              previewLabel={t('bulkEmail.compose.preview')}
              sendLabel={labels.send}
              confirmTitle={labels.confirmTitle}
              confirmDescription={labels.confirmDescription}
              confirmSendLabel={labels.confirmSend}
              canProceed={composer.canProceed}
              isSending={composer.isSending}
              isPreviewing={composer.isPreviewing}
              onEdit={() => composer.setStep('compose')}
              onSendClick={() => composer.setStep('confirm')}
              onConfirmSend={composer.handleSend}
              onTogglePreview={composer.handleTogglePreview}
              cancelLabel={t('common.cancel')}
              onCancel={composer.handleClose}
              senderSlot={senderSlot}
              recipientSlot={recipientSlot}
              programSlot={programSlot}
              subjectSlot={
                <EmailComposerSubjectRow
                  resetKey={composer.resetKey}
                  initialContent={composer.initialSubject}
                  availableVariables={composer.availableVariables}
                  onContentChange={composer.setSubject}
                />
              }
              bodySlot={bodySlot}
            />
          </DialogBody>
        ) : (
          <Box px={6} pb={6}>
            {recapSlot ?? <EmailConnectPrompt />}
          </Box>
        )}

        {/* In-flow overlay anchored to the dialog content, see the positioning
            invariant on SaveAsTemplatePanel. */}
        <SaveAsTemplatePanel
          open={composer.templateFlow.isSaveDrawerOpen}
          seed={composer.templateFlow.saveSeed}
          onClose={composer.templateFlow.closeSaveDrawer}
          onSaved={composer.templateFlow.onTemplateSaved}
        />
      </DialogContent>
    </DialogRoot>
  );
};

export default EmailComposerDialogShell;
